import { CloseOutlined } from '@mui/icons-material';
import { Container, styled, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';

import type { ProjectRunAnalysisQuestion } from '../../../../Types/dealGPT';
import { AnalysisQuestionStatus } from '../../../../Types/enums';
import { FeedbackInterface } from '../../../../Types/feedback';
import { getLatestUniqueQuestions } from '../../utils';
import KPI from './KPI';

const Section = styled('div')({
  border: '1px solid #E9E9E9',
  padding: '5px',
  borderRadius: '2px',
});

const FilterContainer = styled(Container)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: '16px',
  marginTop: '-55px',

  [theme.breakpoints.up('sm')]: {
    padding: '0px',
  },
}));

type AnalysisQuestionFilterProps = {
  selected?: boolean;
};

const AnalysisQuestionFilter = styled(Typography)<AnalysisQuestionFilterProps>(
  ({ selected = false }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '400',
    lineHeight: '125%',
    color: '#092B49',
    backgroundColor: selected ? '#ced7e6' : '#edf1f7',
    borderRadius: '100px',
    padding: '8px 16px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  })
);

interface KPIPageProps {
  pageQuestions?: ProjectRunAnalysisQuestion[];
  refetchQuestions: () => void;
  setIsLoadingThis: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLoadingApplyChanges: React.Dispatch<React.SetStateAction<boolean>>;
  expandAll: boolean;
  handleFeedbackSubmit: (feedback: FeedbackInterface, projectAnalysisQuestionId: string) => void;
  handleValidateFindingsClick: (questionId: string) => void;
}

const KPIPage: React.FC<KPIPageProps> = ({
  pageQuestions,
  refetchQuestions,
  setIsLoadingThis,
  setIsLoadingApplyChanges,
  expandAll,
  handleFeedbackSubmit,
  handleValidateFindingsClick,
}) => {
  const [selectedFilters, setSelectedFilters] = useState<AnalysisQuestionStatus[]>([]);

  const toggleFilters = (filter: AnalysisQuestionStatus) => {
    setSelectedFilters((prevFilters) => {
      const index = prevFilters.indexOf(filter);
      return index > -1 ? prevFilters.filter((f) => f !== filter) : [...prevFilters, filter];
    });
  };

  const isFilterSelected = (filter: AnalysisQuestionStatus) => {
    return selectedFilters.includes(filter);
  };

  const filteredResponses = useMemo(() => {
    if (!pageQuestions) return [];

    const uniqueQuestions = getLatestUniqueQuestions(pageQuestions);

    if (selectedFilters.length === 0) return uniqueQuestions;

    return uniqueQuestions.filter((response) => {
      const notFound =
        response.status === AnalysisQuestionStatus.NOT_FOUND ||
        (response.status === AnalysisQuestionStatus.ANSWERED && !response.value);

      const needsConfirmation =
        response.status === AnalysisQuestionStatus.NEEDS_CONFIRMATION ||
        (response.status === AnalysisQuestionStatus.ANSWERED && response.value);

      const confirmed = response.status === AnalysisQuestionStatus.CONFIRMED;

      return (
        (selectedFilters.includes(AnalysisQuestionStatus.NOT_FOUND) && notFound) ||
        (selectedFilters.includes(AnalysisQuestionStatus.NEEDS_CONFIRMATION) &&
          needsConfirmation) ||
        (selectedFilters.includes(AnalysisQuestionStatus.CONFIRMED) && confirmed)
      );
    });
  }, [pageQuestions, selectedFilters]);

  const handleClearFilters = () => {
    setSelectedFilters([]);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <FilterContainer maxWidth="lg" sx={{ marginBottom: '24px' }}>
          <AnalysisQuestionFilter
            key={AnalysisQuestionStatus.NOT_FOUND}
            selected={isFilterSelected(AnalysisQuestionStatus.NOT_FOUND)}
            onClick={() => toggleFilters(AnalysisQuestionStatus.NOT_FOUND)}
          >
            {'Answer Not Found'}
            {isFilterSelected(AnalysisQuestionStatus.NOT_FOUND) && (
              <CloseOutlined sx={{ fontSize: '16px' }} />
            )}
          </AnalysisQuestionFilter>
          <AnalysisQuestionFilter
            key={AnalysisQuestionStatus.NEEDS_CONFIRMATION}
            selected={isFilterSelected(AnalysisQuestionStatus.NEEDS_CONFIRMATION)}
            onClick={() => toggleFilters(AnalysisQuestionStatus.NEEDS_CONFIRMATION)}
          >
            {'Needs Confirmation'}
            {isFilterSelected(AnalysisQuestionStatus.NEEDS_CONFIRMATION) && (
              <CloseOutlined sx={{ fontSize: '16px' }} />
            )}
          </AnalysisQuestionFilter>
          <AnalysisQuestionFilter
            key={AnalysisQuestionStatus.CONFIRMED}
            selected={isFilterSelected(AnalysisQuestionStatus.CONFIRMED)}
            onClick={() => toggleFilters(AnalysisQuestionStatus.CONFIRMED)}
          >
            {'Confirmed Answer'}
            {isFilterSelected(AnalysisQuestionStatus.CONFIRMED) && (
              <CloseOutlined sx={{ fontSize: '16px' }} />
            )}
          </AnalysisQuestionFilter>
          <Typography
            sx={{
              fontSize: '14px',
              alignSelf: 'center',
              color: '#00a3ff',
              cursor: 'pointer',
            }}
            onClick={() => {
              handleClearFilters();
            }}
          >
            Clear Filters
          </Typography>
        </FilterContainer>
      </div>
      {filteredResponses &&
        filteredResponses.map((kpi: ProjectRunAnalysisQuestion, index: number) => (
          <Section key={`${kpi.pageName}-${kpi.promptType}-${index}`}>
            <KPI
              kpi={kpi}
              setIsLoadingThis={setIsLoadingThis}
              setIsLoadingApplyChanges={setIsLoadingApplyChanges}
              refetchQuestions={refetchQuestions}
              expandAll={expandAll}
              handleFeedbackSubmit={handleFeedbackSubmit}
              handleValidateFindingsClick={handleValidateFindingsClick}
            />
          </Section>
        ))}
    </div>
  );
};

export default KPIPage;
